import { DatePicker, Form, FormItem, TextArea, TextInput, ValidationError } from "client/Components";
import { IFormHandler } from "client/Forms";
import { IObjectInputAttachable } from "client/Forms/attachable";
import { setTrackingId } from "client/Misc/utils";
import { PropsWithChildren, ReactElement } from "react";
import { localize } from "client_legacy/ndaw/culture";
import { ContactAddress, SaveContactModel } from "client/Api/Email/contacts";
import { ContactFieldVisibilityState } from "./EditDialog";
import styles from "./EditForm.module.less";
import { TimeZone } from "client/DateAndTime/timezones";

export type EditFormProps = {
	formHandler: IFormHandler<SaveContactModel>;
	fieldVisibility: ContactFieldVisibilityState;
	onSubmit(): void;
	timezone: TimeZone;
};

export const EditForm = (props: EditFormProps): ReactElement => {
	const {
		formHandler,
		fieldVisibility,
		onSubmit,
		timezone
	} = props;

	return (
		<Form onSubmit={onSubmit} hasWidthConstrained>
			<GridFormSection title={localize("_Name")} trackingId="name">

				<GridFormItem label={localize("_FirstName")} isRequired>
					<TextInput {...formHandler.for("firstName").attachToTextInput()} />
					<ValidationError {...formHandler.for("firstName").attachToValidationError()} />
				</GridFormItem>

				<GridFormItem label={localize("_MiddleName")}>
					<TextInput {...formHandler.for("middleName").attachToTextInput()} />
					<ValidationError {...formHandler.for("middleName").attachToValidationError()} />
				</GridFormItem>

				<GridFormItem label={localize("_LastName")}>
					<TextInput {...formHandler.for("lastName").attachToTextInput()} />
					<ValidationError {...formHandler.for("lastName").attachToValidationError()} />
				</GridFormItem>

			</GridFormSection>

			<GridFormSection title={localize("_ContactInformation")} trackingId="contact-information">

				<GridFormItem label={localize("_EmailAddress")}>
					<TextInput {...formHandler.for("emailAddress").attachToTextInput()} type="email" />
					<ValidationError {...formHandler.for("emailAddress").attachToValidationError()} />
				</GridFormItem>

				<GridFormItem label={localize("_EmailAddress2")} isActivated={fieldVisibility.emailAddress2}>
					<TextInput {...formHandler.for("emailAddress2").attachToTextInput()} type="email" />
					<ValidationError {...formHandler.for("emailAddress2").attachToValidationError()} />
				</GridFormItem>

				<GridFormItem label={localize("_EmailAddress3")} isActivated={fieldVisibility.emailAddress3}>
					<TextInput {...formHandler.for("emailAddress3").attachToTextInput()} type="email" />
					<ValidationError {...formHandler.for("emailAddress3").attachToValidationError()} />
				</GridFormItem>

				<GridFormItem label={localize("_MobilePhone")}>
					<TextInput {...formHandler.for("mobilePhone").attachToTextInput()} type="tel" />
					<ValidationError {...formHandler.for("mobilePhone").attachToValidationError()} />
				</GridFormItem>

				<GridFormItem label={localize("_MobilePhone2")} isActivated={fieldVisibility.mobilePhone2}>
					<TextInput {...formHandler.for("mobilePhone2").attachToTextInput()} type="tel" />
					<ValidationError {...formHandler.for("mobilePhone2").attachToValidationError()} />
				</GridFormItem>

				<GridFormItem label={localize("_HomePhone")} isActivated={fieldVisibility.homePhone}>
					<TextInput {...formHandler.for("homePhone").attachToTextInput()} type="tel" />
					<ValidationError {...formHandler.for("homePhone").attachToValidationError()} />
				</GridFormItem>

				<GridFormItem label={localize("_HomePhone2")} isActivated={fieldVisibility.homePhone2}>
					<TextInput {...formHandler.for("homePhone2").attachToTextInput()} type="tel" />
					<ValidationError {...formHandler.for("homePhone2").attachToValidationError()} />
				</GridFormItem>

				<GridFormItem label={localize("_WorkPhone")} isActivated={fieldVisibility.businessPhone}>
					<TextInput {...formHandler.for("businessPhone").attachToTextInput()} type="tel" />
					<ValidationError {...formHandler.for("businessPhone").attachToValidationError()} />
				</GridFormItem>

				<GridFormItem label={localize("_WorkPhone2")} isActivated={fieldVisibility.businessPhone2}>
					<TextInput {...formHandler.for("businessPhone2").attachToTextInput()} type="tel" />
					<ValidationError {...formHandler.for("businessPhone2").attachToValidationError()} />
				</GridFormItem>

			</GridFormSection>
			<GridFormSection title={localize("_Work")} trackingId="work">

				<GridFormItem label={localize("_JobTitle")}>
					<TextInput {...formHandler.for("jobTitle").attachToTextInput()} />
					<ValidationError {...formHandler.for("jobTitle").attachToValidationError()} />
				</GridFormItem>

				<GridFormItem label={localize("_Department")}>
					<TextInput {...formHandler.for("department").attachToTextInput()} />
					<ValidationError {...formHandler.for("department").attachToValidationError()} />
				</GridFormItem>

				<GridFormItem label={localize("_Company")}>
					<TextInput {...formHandler.for("company").attachToTextInput()} />
					<ValidationError {...formHandler.for("company").attachToValidationError()} />
				</GridFormItem>

			</GridFormSection>

			<AddressFormSection
				title={localize("_HomeAddress")}
				formHandler={formHandler.for("homeAddress")}
				isActivated={fieldVisibility.homeAddress}
				trackingId="home-address"
			/>

			<AddressFormSection
				title={localize("_WorkAddress")}
				formHandler={formHandler.for("businessAddress")}
				isActivated={fieldVisibility.businessAddress}
				trackingId="business-address"
			/>

			<AddressFormSection
				title={localize("_OtherAddress")}
				formHandler={formHandler.for("otherAddress")}
				isActivated={fieldVisibility.otherAddress}
				trackingId="other-address"
			/>
			<GridFormSection
				title={localize("_Other")}
				isActivated={fieldVisibility.birthday || fieldVisibility.notes}
				trackingId="other-form-section"
			>
				<GridFormItem label={localize("_Birthday")} isActivated={fieldVisibility.birthday}>
					<DatePicker
						{...formHandler.for("birthday").attachToDatePicker()}
						mode="date"
						timezone={timezone}
					/>
					<ValidationError {...formHandler.for("birthday").attachToValidationError()} />
				</GridFormItem>

				<GridFormItem label={localize("_Notes")} isActivated={fieldVisibility.notes}>
					<TextArea {...formHandler.for("notes").attachToTextArea()} rows={6} />
					<ValidationError {...formHandler.for("notes").attachToValidationError()} />
				</GridFormItem>

			</GridFormSection>
		</Form>
	);
};
EditForm.displayName="EditForm";

export type AddressFormSectionProps = {
	title: string;
	formHandler: IObjectInputAttachable<ContactAddress>;
	isActivated?: boolean;
	trackingId?: string;
};

export const AddressFormSection = (props: AddressFormSectionProps): ReactElement => {
	const {
		title,
		formHandler,
		isActivated,
		trackingId
	} = props;

	return (
		<GridFormSection title={title} isActivated={isActivated} trackingId={trackingId}>

			<GridFormItem label={localize("_Street")}>
				<TextInput {...formHandler.for("street").attachToTextInput()} />
				<ValidationError {...formHandler.for("street").attachToValidationError()} />
			</GridFormItem>

			<GridFormItem label={localize("_City")}>
				<TextInput {...formHandler.for("city").attachToTextInput()} />
				<ValidationError {...formHandler.for("city").attachToValidationError()} />
			</GridFormItem>

			<GridFormItem label={localize("_Postcode")}>
				<TextInput {...formHandler.for("postcode").attachToTextInput()} />
				<ValidationError {...formHandler.for("postcode").attachToValidationError()} />
			</GridFormItem>

			<GridFormItem label={localize("_StateProvince")}>
				<TextInput {...formHandler.for("state").attachToTextInput()} />
				<ValidationError {...formHandler.for("state").attachToValidationError()} />
			</GridFormItem>

			<GridFormItem label={localize("_CountryRegion")}>
				<TextInput {...formHandler.for("country").attachToTextInput()} />
				<ValidationError {...formHandler.for("country").attachToValidationError()} />
			</GridFormItem>

		</GridFormSection>
	);
};
AddressFormSection.displayName="AddressFormSection";

type GridFormSectionProps = {
	title: string;
	isActivated?: boolean;
	trackingId?: string;
};

const GridFormSection = (props: PropsWithChildren<GridFormSectionProps>): ReactElement | null => {
	const {
		title,
		isActivated = true,
		trackingId,
		children
	} = props;

	if (!isActivated) {
		return null;
	}

	return (
		<section {...setTrackingId(trackingId)} className={styles.section}>
			<h3 {...setTrackingId("section-title")} className={styles.sectionTitle}>{title}</h3>
			<div {...setTrackingId("input-list")}>
				{children}
			</div>
		</section>
	);
};
GridFormSection.displayName="GridFormSection";

type GridFormItemProps = {
	label: string;
	isActivated?: boolean;
	isRequired?: boolean;
};

const GridFormItem = (props: PropsWithChildren<GridFormItemProps>): ReactElement | null => {
	const {
		label,
		isActivated = true,
		isRequired = false,
		children
	} = props;

	if (!isActivated) {
		return null;
	}

	return (
		<FormItem
			label={label}
			isRequired={isRequired}
		>
			{children}
		</FormItem>
	);
};
GridFormItem.displayName="GridFormItem";